import eventbus from 'tiny-emitter/instance';
import { Ref, computed, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue';
import {
  Candidate,
  Line,
  LineArr,
  LineOfPoints,
  PointArr,
  calculate45DegSnapLines,
  distance,
  distanceMany,
  equalCPRight,
  euqalCPLeft,
  getClosestPoint,
  getPointFromEvent,
  indexOfClosestCandidate,
  intersect,
  intersectLineAndRay,
  snapToPointOnLine,
} from './path-tool-utils';

/* TODO:
  - merge together snap points that are the same and merge their lines
  - fix accuracy bugs
  - find a better way to handle line snapping. curved lines snap randomly because the virtual mouse is kinda off?
*/

const SNAP_RANGE = 60;

export const usePathTool = (
  wrapper: Ref<SVGGElement | undefined>,
  nodes: [number, number, number, number, number, number][],
  currentNodeIndexes: Set<number> = new Set()
) => {
  const viewbox = ref<LineArr>([0, 0, 0, 0]);
  const svg = ref<SVGSVGElement>() as Ref<SVGSVGElement>;
  const mouse = ref<[number, number]>([0, 0]);
  const shiftKey = ref(false);
  const virtualMouseDelta = ref<PointArr[]>([]);

  watch(
    () => [...currentNodeIndexes],
    (val) => {
      virtualMouseDelta.value = val
        .map((i) => nodes[i].slice(2, 4) as PointArr)
        .map((p) => {
          const deltaX = mouse.value[0] - p[0];
          const deltaY = mouse.value[1] - p[1];
          return [deltaX, deltaY] as PointArr;
        });
    },
    { deep: true, flush: 'sync' }
  );

  const vMouse = computed(() => {
    let vmd = virtualMouseDelta.value;
    if (virtualMouseDelta.value.length === 0) {
      vmd = [mouse.value];
    }
    return vmd.map((v) => [v[0] + mouse.value[0], v[1] + mouse.value[1]] as PointArr);
  });

  const updateViewbox = () => {
    viewbox.value = svg
      .value!.getAttribute('viewBox')!
      .split(/[\s,]+/)
      .map(Number) as LineArr;
  };

  const onPointerMove = (event: PointerEvent) => {
    const { clientX, clientY } = getPointFromEvent(event, { node: svg.value! });
    mouse.value = [clientX, clientY];
    shiftKey.value = event.shiftKey;
  };

  onMounted(() => {
    svg.value = wrapper.value!.closest<SVGSVGElement>('svg[xmlns]')!;
    svg.value.addEventListener('pointermove', onPointerMove, { capture: true });
    eventbus.on('camera:zoomed', updateViewbox);
    eventbus.on('camera:panned', updateViewbox);
    updateViewbox();
  });

  onBeforeUnmount(() => {
    svg.value!.removeEventListener('pointermove', onPointerMove, { capture: true });
    eventbus.off('camera:zoomed', updateViewbox);
    eventbus.off('camera:panned', updateViewbox);
  });

  const getViewboxLines = () => {
    const vb = viewbox.value!;
    const vbp1: [number, number] = [vb[0], vb[1]];
    const vbp2: [number, number] = [vb[0] + vb[2], vb[1]];
    const vbp3: [number, number] = [vb[0] + vb[2], vb[1] + vb[3]];
    const vbp4: [number, number] = [vb[0], vb[1] + vb[3]];

    const viewboxLines: LineOfPoints[] = [
      [vbp1, vbp2],
      [vbp2, vbp3],
      [vbp3, vbp4],
      [vbp4, vbp1],
    ];

    return viewboxLines;
  };

  const intersectRayWithVb = (ray: Line, viewboxLines: Line[] = getViewboxLines()) => {
    return viewboxLines.map((line) => intersectLineAndRay(line, ray)).filter(Boolean) as LineOfPoints;
  };

  const lineOfInterest = computed(() => {
    const lineSegments = nodes.reduce((acc, curr, index) => {
      if (index < nodes.length - 1) {
        if (currentNodeIndexes.has(index) || currentNodeIndexes.has(index + 1)) return acc;
        if (equalCPRight(curr) && euqalCPLeft(nodes[index + 1])) {
          const p1 = curr.slice(2, 4) as PointArr;
          const p2 = nodes[index + 1].slice(0, 2) as PointArr;
          acc.push([...p1, ...p2]);
        }
      }
      return acc;
    }, [] as LineArr[]);

    if (!lineSegments.length) return;

    const candidates = lineSegments.map((line) => snapToPointOnLine(mouse.value, line));

    const closest = getClosestPoint(mouse.value, candidates, 100);

    const index = candidates.indexOf(closest);

    return lineSegments[index];
  });

  const cross = shallowRef<{ v: LineArr; h: LineArr }>();

  const collectSnapPoints = () => {
    const lines: LineArr[] = [];
    let candidates: Candidate[] = [];

    // The point to check the distance of candidates against. For normal drawing this obviously the mouse
    // when editing, the dragged node(s) should snap and therefore distance is checked against them.
    const distanceCheckPoints = currentNodeIndexes.size ? vMouse.value : [mouse.value];

    // // for each node, add horizontal, vertical and diagonal lines
    // nodes.forEach(([_, __, posX, posY], index) => {
    //   if (currentNodeIndexes.has(index)) return;

    //   // horizontal, vertical, diagonal x2
    //   lines.push(
    //     [posX, posY, posX + 1, posY],
    //     [posX, posY, posX, posY + 1],
    //     [posX, posY, posX + 1, posY + 1],
    //     [posX, posY, posX + 1, posY - 1]
    //   );
    // });

    // for each segment in path, add perpendicular line and 45 degree line going trough the nodes that define the line
    for (let i = 0; i < nodes.length - 1; i++) {
      if (currentNodeIndexes.has(i) || currentNodeIndexes.has(i + 1)) continue;
      const [x1, y1] = nodes[i].slice(2, 4) as PointArr;
      const [x2, y2] = nodes[(i + 1) % nodes.length].slice(2, 4) as PointArr;

      const l = calculate45DegSnapLines([x1, y1], [x2, y2]);
      lines.push(...l);
    }

    if (lineOfInterest.value) {
      lines.push(lineOfInterest.value.flat() as LineArr);
    }

    for (const c1 of lines) {
      for (const c2 of lines) {
        if (c1 === c2) continue;
        const intersection = intersect(c1, c2);
        if (intersection && distanceMany(intersection, distanceCheckPoints) < SNAP_RANGE) {
          candidates.push({
            p: intersection,
            lines: [c1, c2],
          });
        }
      }
    }

    if (nodes.length === 1) {
      const [_, __, posX, posY] = nodes[0];
      lines.push(
        [posX, posY, posX + 1, posY],
        [posX, posY, posX, posY + 1],
        [posX, posY, posX + 1, posY + 1],
        [posX, posY, posX + 1, posY - 1]
      );
    }

    nodes.forEach(([_, __, posX, posY], index) => {
      if (currentNodeIndexes.has(index)) return;

      const mouseLines: LineArr[] = [
        [posX, posY, posX + 1, posY],
        [posX, posY, posX, posY + 1],
        [posX, posY, posX + 1, posY + 1],
        [posX, posY, posX + 1, posY - 1],
      ];

      const points = lines.map((l) => snapToPointOnLine(mouse.value, l));

      points.forEach((p, index) => {
        candidates.some((candidate) => {
          return distance(p, candidate.p) < SNAP_RANGE;
        }) || candidates.push({ p, lines: [lines[index]] });
      });

      // if (lineOfInterest.value) {
      //   const lof = snapToPointOnLine(mouse.value, lineOfInterest.value);
      //   candidates.some((candidate) => {
      //     return distance(candidate.p, lof) < SNAP_RANGE;
      //   }) || candidates.push({ p: lof, lines: [lineOfInterest.value.flat() as LineArr] });
      // }
    });

    if (currentNodeIndexes.size === 2) {
      candidates = [...currentNodeIndexes].flatMap((i, index) => {
        const [deltaX, deltaY] = virtualMouseDelta.value[index];

        return candidates.map((candidate) => {
          return {
            ...candidate,
            p: [candidate.p[0] + deltaX, candidate.p[1] + deltaY],
          };
        });
      });
    }

    // if (svg.value) {
    //   const s = svg.value.instance as import('svg.js').Doc;
    //   document.getElementsByClassName('fooo')[0]?.remove();
    //   const g = s.group().addClass('fooo').style('pointer-events', 'none') as import('svg.js').G;

    //   candidates.forEach((c) => {
    //     g.circle(SNAP_RANGE).center(c.p[0], c.p[1]).opacity(0.1);
    //   });
    // }

    return candidates;
  };

  const closestCandidate = computed(() => {
    const candidates = collectSnapPoints();
    const index = indexOfClosestCandidate(mouse.value, candidates, 100);
    return index !== -1 ? candidates[index] : null;
  });

  const snappedMouse = computed(() => {
    if (shiftKey.value) return mouse.value;

    const candidates = closestCandidate.value;
    return candidates ? candidates.p : mouse.value;
  });

  const indicatorLines = computed(() => {
    if (shiftKey.value) return [];

    const viewboxLines = getViewboxLines();

    return (closestCandidate.value?.lines || []).map((cc) => {
      return intersectRayWithVb(cc, viewboxLines).flat() as LineArr;
    });
  });

  watch(snappedMouse, (snapped) => {
    cross.value = {
      v: [snapped[0], viewbox.value[1], snapped[0], viewbox.value[1] + viewbox.value[3]],
      h: [viewbox.value[0], snapped[1], viewbox.value[0] + viewbox.value[2], snapped[1]],
    };
  });

  return { viewbox, svg, indicatorLines, mouse, cross, snappedMouse };
};
